import { MiniKit } from '@worldcoin/minikit-js';
import { useCallback, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { CONSTANTS } from '@/constants';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useOpenChestStore } from '@/stores/use-open-chest';
import { getMinikitErrorMessage } from '@/utils/errors';
import { sleep } from '@/utils/sleep';
import { waitForTransactionReceipt } from '@/utils/wait-transaction';
import { useFreeChest } from '../countdown/data';
import { toaster } from '../toaster';
import { useNeedVerify } from './use-need-verify';
import { request } from '@/utils/request';

interface SmashResponse {
  success: boolean;
  message: string;
  code: string;
  innerMessage: string | null;
  data: string | { signature: string; refHash: string } | any;
}

function getUrlAndAction(type: string) {
  if (type === 'extraSmash') {
    return {
      action: 'extraSmash',
      url: `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/extra-egg/signature?appId=${CONSTANTS.APP_ID}`,
    };
  }

  return {
    action: 'smash',
    url: `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/signature?appId=${CONSTANTS.APP_ID}`,
  };
}

function openChest(signature: string, requestId?: string) {
  if (!requestId) {
    return MiniKit.commandsAsync.sendTransaction({
      transaction: [
        {
          address: CONSTANTS.SMASH_ADDRESS || '',
          abi: [
            {
              type: 'function',
              name: 'smash',
              inputs: [
                { name: '_proof', type: 'bytes', internalType: 'bytes' },
              ],
              outputs: [],
              stateMutability: 'nonpayable',
            },
          ],
          functionName: 'smash',
          args: [signature],
        },
      ],
    });
  }
  return MiniKit.commandsAsync.sendTransaction({
    transaction: [
      {
        address: CONSTANTS.SMASH_ADDRESS || '',
        abi: [
          {
            type: 'function',
            name: 'extraSmash',
            inputs: [
              { name: '_signature', type: 'bytes', internalType: 'bytes' },
              { name: '_requestId', type: 'bytes32', internalType: 'bytes32' },
            ],
            outputs: [],
            stateMutability: 'nonpayable',
          },
        ],
        functionName: 'extraSmash',
        args: [signature, requestId],
      },
    ],
  });
}

export function useSmash() {
  const address = useWalletAddress();
  const [isLoading, setLoading] = useOpenChestStore(
    useShallow((state) => [state.isLoading, state.setLoading]),
  );
  const { mutate: mutateVerify } = useNeedVerify(address);
  const { mutate: mutateSmash } = useFreeChest();
  const refTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const smash = useCallback(
    async (signature: string, requestId?: string) => {
      try {
        if (!MiniKit.isInstalled()) {
          throw new Error('Minikit is not install');
        }
        setLoading(true);
        refTimeout.current = setTimeout(() => {
          setLoading(false);
        }, 50_000);
        const { finalPayload } = await openChest(signature, requestId);

        if (finalPayload.status === 'error') {
          const errorMessage = getMinikitErrorMessage(finalPayload.error_code);
          throw new Error(errorMessage);
        }
        return finalPayload.transaction_id
        // const txhash = await waitForTransactionReceipt(finalPayload.transaction_id);

        // await sleep(1000);
        // await mutateSmash();
        // mutateVerify();
        // return txhash;
      } catch (error: any) {
        const msg = error?.message || 'Transaction fail';
        toaster.error(msg);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        clearTimeout(refTimeout.current);
      }
    },
    [mutateVerify, mutateSmash, setLoading],
  );

  return { smash, isLoading };
}

export function useSmashV2() {
  const address = useWalletAddress();
  const refTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isLoading, setLoading] = useOpenChestStore(
    useShallow((state) => [state.isLoading, state.setLoading]),
  );

  const { mutate: mutateVerify } = useNeedVerify(address);
  const { mutate: mutateSmash } = useFreeChest();

  const smashV2 = useCallback(
    async (type: 'extraSmash' | 'smash') => {
      try {
        if (!MiniKit.isInstalled()) {
          throw new Error('MiniKit is not installed');
        }
        setLoading(true);

        refTimeout.current = setTimeout(() => {
          setLoading(false);
        }, 50_000);
        const { url } = getUrlAndAction(type);

        const res = await request(url, {
          method: 'POST',
          body: JSON.stringify({
            signal: address,
          }),
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || 'Failed to smash');
        }
        const data: SmashResponse = await res.json();
        console.log('Smash response data:', data);

        const signature =
          type === 'extraSmash' ? data.data.signature : (data.data as string);
        const requestId = type === 'extraSmash' ? data.data.refHash : undefined;
        const { finalPayload } = await openChest(signature, requestId);

        if (finalPayload.status === 'error') {
          const errorMessage = getMinikitErrorMessage(finalPayload.error_code);
          throw new Error(errorMessage);
        }
        return finalPayload.transaction_id
        // const txhash = await waitForTransactionReceipt(finalPayload.transaction_id);

        // await sleep(1000);
        // await mutateSmash();
        // mutateVerify();
        // return txhash;
      } catch (error: any) {
        const msg = error?.message || 'Transaction fail';
        toaster.error(msg);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        clearTimeout(refTimeout.current);
      }
    },
    [setLoading, address, mutateSmash, mutateVerify],
  );

  return {
    smashV2,
    isLoading,
  };
}
